import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError } from '@/api/client';
import { NotAnAdminError, useAuth } from '@/auth/AuthContext';
import { Button, Field, TextInput } from '@/components/ui';
import { setLanguage, SUPPORTED_LANGUAGES, type Language } from '@/i18n';
import { cx } from '@/components/ui';

export function LoginPage() {
  const { t, i18n } = useTranslation();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email.trim(), password);
    } catch (caught) {
      // Three outcomes worth telling apart: wrong credentials, right
      // credentials without admin access, and a server that is not answering.
      // One "login failed" for all three sends people to the wrong fix.
      if (caught instanceof NotAnAdminError) setError(t('login.notAdmin'));
      else if (caught instanceof ApiError && caught.status >= 500)
        setError(t('login.unreachable'));
      else if (caught instanceof ApiError) setError(t('login.wrongCredentials'));
      else setError(t('login.unreachable'));
      setBusy(false);
    }
  }

  return (
    // A landmark rather than a bare div: this screen has no AppShell around it,
    // so without it nothing on the page sits inside one.
    <main className="flex min-h-dvh items-center justify-center bg-chrome px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center gap-2">
          <span aria-hidden className="h-2 w-2 rounded-full bg-accent" />
          <span className="text-sm font-semibold tracking-tight text-white">
            {t('app.name')}
          </span>
        </div>

        <div className="card p-6">
          <h1 className="text-lg font-semibold text-ink">{t('login.title')}</h1>
          <p className="mt-1 text-sm text-ink-subtle">{t('login.subtitle')}</p>

          <form className="mt-5 space-y-4" onSubmit={onSubmit} noValidate>
            <Field label={t('login.email')}>
              {(id, describedBy) => (
                <TextInput
                  id={id}
                  aria-describedby={describedBy}
                  type="email"
                  name="email"
                  autoComplete="username"
                  autoFocus
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              )}
            </Field>

            <Field label={t('login.password')}>
              {(id, describedBy) => (
                <TextInput
                  id={id}
                  aria-describedby={describedBy}
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              )}
            </Field>

            {error ? (
              <p role="alert" className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-200">
                {error}
              </p>
            ) : null}

            <Button
              type="submit"
              variant="primary"
              loading={busy}
              className="w-full"
              disabled={!email || !password}
            >
              {busy ? t('login.submitting') : t('login.submit')}
            </Button>
          </form>
        </div>

        <div className="mt-4 flex justify-center gap-1">
          {SUPPORTED_LANGUAGES.map((language) => (
            <button
              key={language}
              type="button"
              onClick={() => setLanguage(language as Language)}
              aria-pressed={i18n.language === language}
              className={cx(
                'min-h-[2rem] rounded-md px-3 text-xs font-medium uppercase transition-colors',
                i18n.language === language
                  ? 'bg-white/15 text-white'
                  : 'text-white/50 hover:bg-white/10',
              )}
            >
              {language}
            </button>
          ))}
        </div>
      </div>
    </main>
  );
}
